import * as React from 'react';
import { Link } from 'react-router-dom';

import { Badge } from '@/shared/ui/badge';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/shared/ui/table';

import type { DashboardWorkOrder } from '../types';
import { normalizeWorkOrderStatus } from '../types';

interface AdminWorkOrdersTableProps {
  workOrders: DashboardWorkOrder[];
}

interface StatusMeta {
  label: string;
  variant: 'default' | 'secondary' | 'warning' | 'success' | 'muted';
}

const STATUS_META: Record<number, StatusMeta> = {
  1: { label: 'Draft', variant: 'muted' },
  2: { label: 'Planned', variant: 'default' },
  3: { label: 'In Progress', variant: 'warning' },
  4: { label: 'Closed', variant: 'success' },
};

/**
 * Active work orders table card for the Operations Command Center.
 */
export function AdminWorkOrdersTable({ workOrders }: AdminWorkOrdersTableProps) {
  if (workOrders.length === 0) {
    return (
      <div className="tw-py-6 tw-text-center tw-text-sm tw-text-muted-foreground">
        No active work orders.
      </div>
    );
  }

  return (
    <Table>
      <TableHeader>
        <TableRow>
          <TableHead className="tw-text-xs tw-uppercase tw-tracking-wide">
            Work Order
          </TableHead>
          <TableHead className="tw-text-xs tw-uppercase tw-tracking-wide">
            Client
          </TableHead>
          <TableHead className="tw-text-xs tw-uppercase tw-tracking-wide">
            Status
          </TableHead>
          <TableHead className="tw-text-xs tw-uppercase tw-tracking-wide">
            Date
          </TableHead>
        </TableRow>
      </TableHeader>
      <TableBody>
        {workOrders.map((wo) => {
          const status = normalizeWorkOrderStatus(wo.status);
          const meta = STATUS_META[status] ?? { label: 'Unknown', variant: 'muted' as const };
          return (
            <TableRow key={wo.id}>
              <TableCell>
                <Link
                  to={`/clients/work-orders/view/${wo.id}`}
                  className="tw-font-medium tw-text-foreground hover:tw-text-primary"
                >
                  {wo.workOrderNumber || `WO-${wo.id}`}
                </Link>
              </TableCell>
              <TableCell className="tw-text-muted-foreground">
                {wo.clientName || 'N/A'}
              </TableCell>
              <TableCell>
                <Badge variant={meta.variant}>{meta.label}</Badge>
              </TableCell>
              <TableCell className="tw-text-muted-foreground">
                {wo.created && !Number.isNaN(new Date(wo.created).getTime())
                  ? new Date(wo.created).toLocaleDateString()
                  : 'N/A'}
              </TableCell>
            </TableRow>
          );
        })}
      </TableBody>
    </Table>
  );
}
